/**
 * Vehicle DNA panel — the benchmark hero on the VehicleDetail page.
 *
 * Puts four consumption reference points on one axis so the answer to "is
 * this vehicle drinking too much?" is a glance rather than a spreadsheet:
 *
 *   OEM factory  ·  Peers  ·  Own baseline  ·  Actual (90 days)
 *
 * Only the ACTUAL bar is colour-coded, and only against the peer cohort
 * (falling back to OEM) — see `actualTone`. The other three are neutral
 * teal reference marks; tinting them too would imply a verdict on a
 * benchmark, which is meaningless.
 *
 * Everything except the vehicle identity is nullable on the wire. Missing
 * benchmarks drop their bar and are named in a muted note beneath the
 * chart, so a sparse tenant sees "we don't have this" instead of a zero
 * that reads as "this vehicle uses no fuel".
 *
 * On 404 / error the panel renders NOTHING — the rest of VehicleDetail
 * must be unaffected by a missing DNA row.
 */

import { Dna, Gauge, Target, Wallet } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { KpiCard, Panel, StatusBadge, type StatusTone } from '@/components/fleetguardian';
import { Skeleton } from '@/components/ui/skeleton';
import { useVehicleDna } from '@/api/hooks/useVehicleDna';
import {
  CHART_GRID_STROKE,
  CHART_PALETTE,
  chartAxis,
  chartTooltipItemStyle,
  chartTooltipLabelStyle,
  chartTooltipStyle,
} from '@/lib/chartTheme';
import {
  actualTone,
  excessCostDisplay,
  fmtKm,
  fmtLPer100,
  fmtRPerKm,
  fmtRPerLitre,
  toNum,
  TONE_FILL,
  TONE_WORD,
  type DnaTone,
} from './vehicleDnaLogic';

const BAR_ROW_PX = 46;
const CHART_CHROME_PX = 44;
/** Headroom so the tallest bar (or the p75 band edge) isn't flush to the frame. */
const DOMAIN_HEADROOM = 1.08;

const TONE_STATUS: Record<DnaTone, StatusTone> = {
  good: 'success',
  warn: 'warning',
  bad: 'danger',
  neutral: 'neutral',
};

const TONE_TEXT_CLASS: Record<DnaTone, string> = {
  good: 'text-emerald-600 dark:text-emerald-400',
  warn: 'text-amber-600 dark:text-amber-400',
  bad: 'text-red-600 dark:text-red-400',
  neutral: '',
};

interface DnaBar {
  key: string;
  label: string;
  value: number;
  fill: string;
  /** Full sentence shown in the tooltip — carries the peer definition. */
  descriptor: string;
}

export interface VehicleDnaPanelProps {
  /** Resolved from the vehicle profile; the query stays idle until it lands. */
  registration: string | undefined;
}

function PanelShell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <Panel className="p-4 sm:p-5">
      <div className="space-y-4">{children}</div>
    </Panel>
  );
}

export function VehicleDnaPanel({ registration }: VehicleDnaPanelProps): JSX.Element | null {
  const { data, isLoading, isError } = useVehicleDna(registration);

  if (!registration) return null;

  if (isLoading) {
    return (
      <PanelShell>
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-[220px] w-full" />
        <div className="grid gap-3 sm:grid-cols-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </PanelShell>
    );
  }

  // A vehicle with no DNA row 404s. Render nothing rather than an error card —
  // the page's map/stations content is the primary payload and must stand alone.
  if (isError || !data) return null;

  const { vehicle, oem, cohort, own, pricing, cost, coverage_notes: notes } = data;

  const oemValue = toNum(oem?.consumption_l_per_100km);
  const cohortValue = toNum(cohort?.median_l_per_100km);
  const baselineValue = toNum(own?.baseline_l_per_100km);
  const actualValue = toNum(own?.actual_l_per_100km_90d);

  const tone = actualTone(actualValue, cohort, oemValue);

  const cohortSample = toNum(cohort?.sample_size);
  const baselineSample = toNum(own?.baseline_sample_size);
  const p25 = toNum(cohort?.p25_l_per_100km);
  const p75 = toNum(cohort?.p75_l_per_100km);

  const candidates: Array<Omit<DnaBar, 'value'> & { value: number | null }> = [
    {
      key: 'oem',
      label: 'OEM factory',
      value: oemValue,
      fill: CHART_PALETTE[0],
      descriptor: oem?.variant_label
        ? `Factory spec — ${oem.variant_label}`
        : 'Factory spec for this variant',
    },
    {
      key: 'cohort',
      label: 'Peers',
      value: cohortValue,
      fill: CHART_PALETTE[1],
      descriptor: `Peer median — same class + application + zone${
        cohortSample !== null ? ` (n=${cohortSample})` : ''
      }`,
    },
    {
      key: 'baseline',
      label: 'Own baseline',
      value: baselineValue,
      fill: CHART_PALETTE[2],
      descriptor: `This vehicle's historical rate${
        baselineSample !== null ? ` (n=${baselineSample})` : ''
      }`,
    },
    {
      key: 'actual',
      label: 'Actual (90 days)',
      value: actualValue,
      fill: TONE_FILL[tone],
      descriptor: `Last 90 days — ${TONE_WORD[tone]}`,
    },
  ];

  const bars: DnaBar[] = candidates
    .filter((c): c is DnaBar => c.value !== null)
    .map((c) => ({ ...c, value: c.value }));
  const missing = candidates.filter((c) => c.value === null).map((c) => c.label);

  const hasBand = p25 !== null && p75 !== null && p75 > p25;
  const domainMax =
    bars.length > 0
      ? Math.max(...bars.map((b) => b.value), hasBand ? (p75 as number) : 0) * DOMAIN_HEADROOM
      : 0;

  const excess = excessCostDisplay(cost?.excess_cost_90d_vs_oem);
  const totalKm = toNum(own?.total_km_90d);

  const chips: Array<{ key: string; label: string; value: string }> = [];
  if (vehicle.vehicle_class) {
    chips.push({ key: 'class', label: 'Class', value: vehicle.vehicle_class });
  }
  if (vehicle.adg_code) {
    chips.push({ key: 'adg', label: 'Application', value: vehicle.adg_code });
  }
  if (pricing?.zone_code) {
    chips.push({ key: 'zone', label: 'Zone', value: pricing.zone_code });
  }

  return (
    <PanelShell>
      {/* 1 — header row: title + identity chips */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-[15px] font-bold leading-tight text-gray-900 dark:text-foreground">
            <Dna className="h-4 w-4 text-teal-700 dark:text-teal-300" />
            Vehicle DNA
          </h3>
          <p className="mt-0.5 text-[13px] text-gray-500 dark:text-muted-foreground">
            Real-world consumption against every benchmark we hold for this vehicle
            {hasBand ? ' — shaded band is the peer p25–p75 range.' : '.'}
          </p>
        </div>
        {chips.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {chips.map((chip) => (
              <StatusBadge key={chip.key} tone="neutral" showDot={false}>
                <span className="text-gray-500 dark:text-muted-foreground">{chip.label}</span>
                <span className="font-semibold">{chip.value}</span>
              </StatusBadge>
            ))}
          </div>
        )}
      </div>

      {/* 2 — the hero: four benchmarks on one axis */}
      {bars.length > 0 ? (
        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-muted-foreground">
              Consumption · L/100km
            </span>
            {actualValue !== null && (
              <StatusBadge tone={TONE_STATUS[tone]}>{TONE_WORD[tone]}</StatusBadge>
            )}
          </div>
          <div style={{ height: bars.length * BAR_ROW_PX + CHART_CHROME_PX }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={bars}
                margin={{ top: 4, right: 48, bottom: 4, left: 8 }}
              >
                <CartesianGrid horizontal={false} stroke={CHART_GRID_STROKE} />
                <XAxis
                  type="number"
                  domain={[0, domainMax]}
                  {...chartAxis}
                  tickFormatter={(v: number) => v.toFixed(0)}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  {...chartAxis}
                  width={104}
                  tick={{ ...chartAxis.tick, fontSize: 11 }}
                />
                {/* Peer interquartile band — drawn behind the bars so "inside
                    the band = normal" reads without a legend lookup. */}
                {hasBand && (
                  <ReferenceArea
                    x1={p25 as number}
                    x2={p75 as number}
                    fill={CHART_PALETTE[2]}
                    fillOpacity={0.12}
                    ifOverflow="extendDomain"
                  />
                )}
                <Tooltip
                  cursor={{ fill: 'rgba(31,107,117,0.06)' }}
                  contentStyle={chartTooltipStyle}
                  itemStyle={chartTooltipItemStyle}
                  labelStyle={chartTooltipLabelStyle}
                  formatter={(value: number, _name, entry) => [
                    fmtLPer100(value),
                    (entry?.payload as DnaBar | undefined)?.descriptor ?? 'Consumption',
                  ]}
                />
                <Bar
                  dataKey="value"
                  name="Consumption"
                  radius={[0, 4, 4, 0]}
                  barSize={20}
                  isAnimationActive={false}
                >
                  {bars.map((b) => (
                    <Cell key={b.key} fill={b.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {missing.length > 0 && (
            <p className="mt-1 text-xs text-gray-400 dark:text-muted-foreground">
              No data for: {missing.join(', ')}.
            </p>
          )}
        </div>
      ) : (
        <p className="text-xs text-gray-400 dark:text-muted-foreground">
          No consumption benchmarks available for this vehicle yet.
        </p>
      )}

      {/* 3 — operating cost */}
      <div className="grid gap-3 sm:grid-cols-3">
        <KpiCard
          label="R/km actual"
          value={fmtRPerKm(cost?.r_per_km_actual)}
          icon={Gauge}
          hue="navy"
          subtitle={totalKm !== null ? `over ${fmtKm(totalKm)} in 90 days` : undefined}
        />
        <KpiCard
          label="R/km expected (OEM)"
          value={fmtRPerKm(cost?.r_per_km_oem_expected)}
          icon={Target}
          hue="blue"
          subtitle="factory consumption at zone price"
        />
        <KpiCard
          label="Excess cost — last 90 days"
          value={<span className={TONE_TEXT_CLASS[excess.tone]}>{excess.text}</span>}
          icon={Wallet}
          hue={excess.tone === 'bad' ? 'red' : excess.tone === 'good' ? 'emerald' : 'gray'}
          subtitle="actual vs OEM benchmark × km travelled"
        />
      </div>

      {/* 4 — price context */}
      {pricing && (
        <p className="text-xs text-gray-500 dark:text-muted-foreground">
          Paid avg{' '}
          <span className="font-medium tabular-nums text-gray-700 dark:text-foreground">
            {fmtRPerLitre(pricing.avg_price_paid_per_litre_90d)}
          </span>{' '}
          vs regulated{' '}
          <span className="font-medium tabular-nums text-gray-700 dark:text-foreground">
            {fmtRPerLitre(pricing.regulated_price_per_litre)}
          </span>
          {pricing.zone_code ? ` in zone ${pricing.zone_code}` : ''}
        </p>
      )}

      {/* 5 — coverage footnotes */}
      {notes && notes.length > 0 && (
        <ul className="space-y-0.5 border-t border-gray-200 pt-3 dark:border-white/10">
          {notes.map((note) => (
            <li key={note} className="text-[11px] leading-snug text-gray-400 dark:text-muted-foreground">
              {note}
            </li>
          ))}
        </ul>
      )}
    </PanelShell>
  );
}

export default VehicleDnaPanel;
